import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Result, Button, Card, Spin, message } from "antd";
import {
  CheckCircleOutlined,
  HomeOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { gql, useLazyQuery } from "@apollo/client";
import { setUserInfo } from "../../../store/features/UserInfo";
import { Container } from "./PricingStyle";

const GET_COACH_SUBSCRIPTION_STATUS = gql`
  query getCoachSubscriptionStatus {
    getCoachSubscriptionStatus {
      status
      hasActiveSubscription
      trialEndDate
      trialStartDate
      currentPeriodStart
      currentPeriodEnd
      cancelAtPeriodEnd
      planType
    }
  }
`;

export default function SubscriptionSuccess() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState(null);

  const [getCoachSubscriptionStatus] = useLazyQuery(
    GET_COACH_SUBSCRIPTION_STATUS,
    {
      fetchPolicy: "network-only",
    }
  );

  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      try {
        setLoading(true);
        // Fetch updated subscription status after successful payment
        const result = await getCoachSubscriptionStatus();
        const data = result?.data?.getCoachSubscriptionStatus;

        if (data) {
          setSubscriptionData(data);
          // Update store with latest subscription status
          const userInfo = JSON.parse(
            localStorage.getItem("uvita-userInfo") || "{}"
          );
          dispatch(
            setUserInfo({
              ...userInfo,
              subscriptionStatus: data,
            })
          );

          // Trigger custom event for subscription modal
          window.dispatchEvent(new CustomEvent("subscription-success"));

          message.success(
            "Payment successful! Your subscription is now active."
          );
        }
      } catch (error) {
        console.error("Failed to fetch subscription status:", error);
        message.warning(
          "Payment completed, but failed to fetch subscription details. Please refresh the page."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptionStatus();
  }, [getCoachSubscriptionStatus, dispatch]);

  const planName =
    subscriptionData?.planType === "YEARLY" ||
    subscriptionData?.planType === "yearly"
      ? "Yearly"
      : "Monthly";

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <Container>
        <div className="success-container">
          <Card
            className="success-card"
            style={{ textAlign: "center", padding: "60px 20px" }}
          >
            <Spin size="large" />
            <p style={{ marginTop: "20px", fontSize: "16px" }}>
              Loading subscription details...
            </p>
          </Card>
        </div>
      </Container>
    );
  }

  const isTrialing =
    subscriptionData?.status === "TRIALING" ||
    subscriptionData?.status === "TRIAL";
  const trialEndDate = subscriptionData?.trialEndDate;

  return (
    <Container>
      <div className="success-container">
        <Card className="success-card">
          <Result
            icon={<CheckCircleOutlined style={{ color: "#52c41a" }} />}
            title={
              isTrialing ? "Free Trial Started!" : "Subscription Activated!"
            }
            subTitle={
              <div className="success-details">
                <p style={{ fontSize: "16px", marginBottom: "20px" }}>
                  <strong>{planName}</strong> plan activated.
                </p>

                {isTrialing && trialEndDate && (
                  <div
                    style={{
                      background: "#e6f7ff",
                      padding: "16px",
                      borderRadius: "8px",
                      marginBottom: "16px",
                      border: "1px solid #91d5ff",
                      textAlign: "left",
                    }}
                  >
                    <p style={{ margin: "4px 0" }}>
                      Your free trial is active until {formatDate(trialEndDate)}
                      .
                    </p>
                  </div>
                )}
              </div>
            }
            extra={[
              <Button
                type="primary"
                size="large"
                icon={<HomeOutlined />}
                onClick={() => navigate("/")}
                key="dashboard"
              >
                Go to Dashboard
              </Button>,
              <Button
                size="large"
                icon={<SettingOutlined />}
                onClick={() => navigate("/AccountSettings")}
                key="account"
              >
                Manage Subscription
              </Button>,
            ]}
          />
        </Card>
      </div>
    </Container>
  );
}
